// PermissionGuard — Settings-03.
//
// Defensive route-level wrapper. Renders ``children`` when the
// active membership's ``permissions`` include ``permission``;
// renders a redirect otherwise. Pair with ``ProtectedRoute`` —
// auth comes first, then this gate. Hidden nav links shouldn't be
// the only thing standing between a member and a settings URL,
// so every gated page mounts under one of these.

import { Navigate } from "react-router-dom";

import type { ReactNode } from "react";

import { usePermissions, type PermissionFlags } from "./usePermissions";


interface PermissionGuardProps {
  // The flag the active membership must have set to ``true``. Keys
  // mirror the ``AuthPermissions`` shape, plus the bridge flags
  // exposed by ``usePermissions`` (``isProducer`` / ``role``) so
  // future surfaces can branch on them too.
  permission: keyof PermissionFlags;
  // Where to send a user who doesn't have the permission. Defaults
  // to ``/dashboard`` — every authenticated member can reach it.
  fallbackPath?: string;
  children: ReactNode;
}


export function PermissionGuard({
  permission, fallbackPath = "/dashboard", children,
}: PermissionGuardProps) {
  const flags = usePermissions();
  const allowed = Boolean(flags[permission]);
  if (!allowed) {
    return <Navigate to={fallbackPath} replace />;
  }
  return <>{children}</>;
}
